// RGPD / GDPR — Politique de protection des données personnelles
// Multilingual (FR/EN), fallback FR. Conforms to French CNIL + EU GDPR baseline:
// data controller identity, lawful bases, data categories, retention, recipients,
// data subject rights (access, rectification, erasure, portability, restriction,
// objection, withdraw consent), complaint to CNIL, cookies, contact.
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import SeoHead from "@/components/SeoHead";

const COPY = {
  fr: {
    kicker: "Vie privée",
    title: "Protection des données personnelles (RGPD)",
    updated: "Dernière mise à jour : 16 février 2026",
    sections: [
      {
        h: "1. Responsable du traitement",
        p: [
          "Les présentes données personnelles sont traitées par <strong>Les Amis du Prieuré saint Léonard</strong>, association loi 1901, dont le siège est situé Prieuré saint Léonard, 50300 Vains, France.",
          "Pour toute question relative à vos données personnelles, vous pouvez écrire à <a href='mailto:contact@prieure-saint-leonard.fr'>contact@prieure-saint-leonard.fr</a>.",
        ],
      },
      {
        h: "2. Données collectées et finalités",
        p: [
          "Nous collectons uniquement les données strictement nécessaires aux finalités suivantes :",
          "<ul><li><strong>Adhésion et espace membre</strong> : nom, prénom, e-mail, téléphone, rôle (organisateur, bénévole, artiste…). Base légale : <em>contrat d'adhésion</em>.</li>" +
          "<li><strong>Inscription aux Fêtes 2027 et événements</strong> : nom, e-mail, choix d'activités, paiement (via SumUp). Base légale : <em>exécution d'un contrat</em>.</li>" +
          "<li><strong>Demandes Bergerie pèlerine / Séjour au Prieuré</strong> : coordonnées, dates, nombre de personnes. Base légale : <em>mesures précontractuelles</em>.</li>" +
          "<li><strong>Newsletter</strong> : e-mail, prénom, langue. Base légale : <em>consentement</em> (révocable à tout moment via le lien de désinscription).</li>" +
          "<li><strong>Dons</strong> : nom, e-mail, montant (traité par Stripe). Base légale : <em>obligation légale comptable</em> + <em>reçu fiscal</em>.</li>" +
          "<li><strong>Formulaire de contact</strong> : nom, e-mail, message. Base légale : <em>intérêt légitime</em>.</li></ul>",
        ],
      },
      {
        h: "3. Destinataires de vos données",
        p: [
          "Vos données ne sont jamais vendues ni cédées à des tiers à des fins commerciales. Elles sont accessibles :",
          "<ul><li>aux organisateurs et administrateurs de l'association (espace APLEO), liés par une obligation de confidentialité,</li>" +
          "<li>à nos sous-traitants strictement nécessaires : <strong>Brevo</strong> (envoi d'e-mails), <strong>SumUp</strong> (billetterie), <strong>Stripe</strong> (dons), <strong>Qonto</strong> (comptabilité bancaire), <strong>Emergent</strong> (hébergement Cloud, stockage objet, infrastructure),</li>" +
          "<li>aux autorités habilitées en cas de réquisition légale.</li></ul>",
        ],
      },
      {
        h: "4. Durée de conservation",
        p: [
          "<ul><li><strong>Comptes membres actifs</strong> : durée de l'adhésion + 3 ans après désactivation.</li>" +
          "<li><strong>Inscriptions aux événements</strong> : 3 ans après la date de l'événement.</li>" +
          "<li><strong>Données comptables et reçus fiscaux</strong> : 10 ans (obligation légale).</li>" +
          "<li><strong>Newsletter</strong> : jusqu'à désinscription, puis 3 ans en logs anonymisés.</li>" +
          "<li><strong>Messages de contact</strong> : 3 ans après le dernier échange.</li></ul>",
        ],
      },
      {
        h: "5. Vos droits",
        p: [
          "Conformément au RGPD (Règlement UE 2016/679) et à la loi Informatique et Libertés, vous disposez :",
          "<ul><li>d'un <strong>droit d'accès</strong> à vos données,</li>" +
          "<li>d'un <strong>droit de rectification</strong>,</li>" +
          "<li>d'un <strong>droit à l'effacement</strong> (« droit à l'oubli »),</li>" +
          "<li>d'un <strong>droit à la portabilité</strong>,</li>" +
          "<li>d'un <strong>droit à la limitation du traitement</strong>,</li>" +
          "<li>d'un <strong>droit d'opposition</strong>,</li>" +
          "<li>du <strong>droit de retirer votre consentement</strong> à tout moment,</li>" +
          "<li>du <strong>droit de définir des directives post-mortem</strong> sur le sort de vos données.</li></ul>",
          "Pour exercer ces droits, écrivez à <a href='mailto:contact@prieure-saint-leonard.fr'>contact@prieure-saint-leonard.fr</a> en précisant l'objet de votre demande. Nous répondons dans un délai d'un mois.",
        ],
      },
      {
        h: "6. Réclamation auprès de la CNIL",
        p: [
          "Si vous estimez, après nous avoir contactés, que vos droits sur vos données ne sont pas respectés, vous pouvez introduire une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés) — 3 place de Fontenoy, 75007 Paris — <a href='https://www.cnil.fr' target='_blank' rel='noreferrer noopener'>www.cnil.fr</a>.",
        ],
      },
      {
        h: "7. Cookies et traceurs",
        p: [
          "Ce site utilise uniquement les cookies <strong>strictement nécessaires</strong> à son fonctionnement (préférence de langue, session d'authentification, panier d'inscription). Aucun cookie publicitaire ou de profilage n'est déposé.",
          "Un bandeau d'information vous est présenté lors de votre première visite. Vous pouvez modifier vos préférences à tout moment depuis votre navigateur.",
        ],
      },
      {
        h: "8. Sécurité",
        p: [
          "Les données sont stockées dans des bases hébergées au sein de l'Union européenne, protégées par chiffrement au repos et en transit (HTTPS). Les mots de passe sont hashés avec bcrypt et ne sont jamais accessibles, même à l'administrateur. Des sauvegardes automatiques chiffrées sont effectuées régulièrement.",
        ],
      },
    ],
  },
  en: {
    kicker: "Privacy",
    title: "Personal data protection (GDPR)",
    updated: "Last updated: 16 February 2026",
    sections: [
      {
        h: "1. Data controller",
        p: [
          "Personal data on this site is processed by <strong>The Friends of the Priory of Saint Leonard</strong> (Les Amis du Prieuré saint Léonard), French non-profit association (1901 Act), with headquarters at Prieuré saint Léonard, 50300 Vains, France.",
          "For any question, please write to <a href='mailto:contact@prieure-saint-leonard.fr'>contact@prieure-saint-leonard.fr</a>.",
        ],
      },
      {
        h: "2. Data collected and purposes",
        p: [
          "We only collect data strictly necessary for the following purposes: membership management, event registration, Sheepfold/Stay requests, newsletter (consent-based), donations (legal compliance), contact form (legitimate interest).",
        ],
      },
      {
        h: "3. Data recipients",
        p: [
          "Your data is never sold. It is accessible to the association's organisers (under confidentiality) and to our processors strictly necessary: <strong>Brevo</strong> (emails), <strong>SumUp</strong> (ticketing), <strong>Stripe</strong> (donations), <strong>Qonto</strong> (banking), <strong>Emergent</strong> (hosting).",
        ],
      },
      {
        h: "4. Retention period",
        p: [
          "Active members: duration of membership + 3 years. Event registrations: 3 years. Accounting records: 10 years (legal). Newsletter: until unsubscribe.",
        ],
      },
      {
        h: "5. Your rights",
        p: [
          "Under GDPR (EU Regulation 2016/679), you have the right to access, rectify, erase, port, restrict, object, and withdraw consent at any time. Write to <a href='mailto:contact@prieure-saint-leonard.fr'>contact@prieure-saint-leonard.fr</a> — we respond within one month.",
        ],
      },
      {
        h: "6. Complaint to the CNIL",
        p: [
          "If you believe your rights are not respected, you may file a complaint with the French data authority — <strong>CNIL</strong>, 3 place de Fontenoy, 75007 Paris — <a href='https://www.cnil.fr' target='_blank' rel='noreferrer noopener'>www.cnil.fr</a>.",
        ],
      },
      {
        h: "7. Cookies",
        p: [
          "This site only uses cookies <strong>strictly necessary</strong> for operation (language preference, authentication session, registration cart). No advertising or profiling cookies.",
        ],
      },
      {
        h: "8. Security",
        p: [
          "Data is stored in EU-hosted databases, encrypted at rest and in transit (HTTPS). Passwords are bcrypt-hashed. Automated encrypted backups are performed regularly.",
        ],
      },
    ],
  },
};

export default function RGPD() {
  const { locale } = useI18n();
  const t = COPY[locale] || COPY.fr;
  return (
    <div data-testid="rgpd-page" className="py-20 sm:py-28">
      <SeoHead
        titles={{ fr: "Protection des données (RGPD) · Prieuré saint Léonard",
                  en: "Data Protection (GDPR) · Priory of Saint Leonard" }}
        descriptions={{ fr: t.title, en: t.title }}
      />
      <div className="container-prieure max-w-3xl">
        <div className="text-center">
          <div className="font-caps text-[0.7rem]" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
            {t.kicker}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3" data-testid="rgpd-title">
            {t.title}
          </h1>
          <Ornament className="mx-auto mt-5" />
          <p className="font-body italic text-sm mt-4" style={{ color: "var(--muted)" }}>
            {t.updated}
          </p>
        </div>
        <div className="mt-12 space-y-10">
          {t.sections.map((s, i) => (
            <section key={i} data-testid={`rgpd-section-${i}`}>
              <h2 className="font-display text-2xl mb-3" style={{ color: "var(--ink)" }}>
                {s.h}
              </h2>
              {s.p.map((para, j) => (
                <div key={j}
                     className="font-body text-base leading-relaxed mb-3"
                     style={{ color: "var(--ink-2)" }}
                     dangerouslySetInnerHTML={{ __html: para }} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
